import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/catalyst/Button";
import {
	Dialog,
	DialogActions,
	DialogBody,
	DialogDescription,
	DialogTitle,
} from "@/components/catalyst/Dialog";
import { StripePaymentForm } from "@/components/stripe-payment-form";
import { orpc } from "@/orpc/client";

export function UpdatePaymentMethodDialog({
	isOpen,
	onClose,
}: {
	isOpen: boolean;
	onClose: () => void;
}) {
	const queryClient = useQueryClient();

	const { data } = useSuspenseQuery({
		...orpc.billing.createSetupIntent.queryOptions(),
		retry: 1,
		staleTime: Number.POSITIVE_INFINITY,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});

	const handleSuccess = () => {
		queryClient.invalidateQueries({
			queryKey: orpc.billing.getBillingDetails.queryOptions().queryKey,
		});
		onClose();
	};

	return (
		<Dialog open={isOpen} onClose={onClose}>
			<DialogTitle>Update Payment Method</DialogTitle>
			<DialogDescription>
				Enter your new card details below. Your payment information is securely
				handled by Stripe.
			</DialogDescription>
			<DialogBody>
				<StripePaymentForm
					clientSecret={data.clientSecret}
					className="space-y-4"
					onSubmit={async ({ stripe, elements }) => {
						const { error } = await stripe.confirmSetup({
							elements,
							confirmParams: {
								return_url: window.location.href,
							},
							redirect: "if_required",
						});

						if (error) {
							return error.message ?? "An unexpected error occurred.";
						}

						toast.success("Payment method updated successfully!");
						handleSuccess();
						return undefined;
					}}
				>
					{({ paymentElement, error, isProcessing, isReady }) => (
						<>
							{paymentElement}
							{error}
							<Button
								type="submit"
								color="dark/white"
								className="w-full"
								disabled={!isReady || isProcessing}
							>
								{isProcessing ? "Processing..." : "Save Payment Method"}
							</Button>
						</>
					)}
				</StripePaymentForm>
			</DialogBody>
			<DialogActions>
				<Button outline onClick={onClose}>
					Cancel
				</Button>
			</DialogActions>
		</Dialog>
	);
}
